<script setup>
    // Component
    import MainHeader from '@/components/HeaderComponent.vue';

    // Store
    import { useAgreementDetailStore } from '@/stores/modal/agreementDetailStore';
    import { useHeaderStore } from '@/stores/header/headerStore';
    import { useLanguageStore } from '@/stores/common/languageStore';
    import { useLoginStore } from '@/stores/login/loginStore';
    import { useSetupAgreeStore } from '@/stores/setup/setupAgreeStore';
    import { useButtonDisabledStore } from '@/stores/common/disabledStore';

    // Vue
    import { getCurrentInstance, inject, onMounted, ref } from 'vue';
    import { useRouter } from 'vue-router';

    // Utis
    import utils from '@/commonProject/helpers/utils';

    // Else
    import i18n from '@/common/i18n';

    // ----------------------------
    // 定数
    // ----------------------------
    const app = getCurrentInstance();

    // メッセージダイアログ取得。
    const { messageDialog } = inject('messageDialogKey');

    const router = useRouter();

    const loginStore = useLoginStore();

    const headerStore = useHeaderStore();

    const setupAgreeStore = useSetupAgreeStore();

    const agreementDetailStore = useAgreementDetailStore();

    // ボタン連打ガード制御
    const disabledStore = useButtonDisabledStore();

    // ----------------------------
    // 言語情報取得
    // ----------------------------
    const languageStore = useLanguageStore();

    const agreeDisabledFlag = ref(true);

    const regulationDt = ref('');

    const regulationNm = ref('');

    const regulationContent = ref('');

    onMounted(async () => {
        // 初期化する。
        agreeDisabledFlag.value = true;
        regulationDt.value = '';
        regulationNm.value = '';
        regulationContent.value = '';

        // 規定詳細取得APIのクエリパラメーターを生成する。
        const queryParam = {
            Lang: languageStore.getLanguageValue,
            RegulationPk: loginStore.regulationPk,
            // RegulationPk: setupAgreeStore.getRegulationPk,
        };

        // 規定詳細取得APIをコールする。
        const response = await agreementDetailStore.getAgreementDetailAction(app.appContext.config.globalProperties.$constProperties.BASE_API_URL_S, queryParam);
        if (response.success) {
            regulationDt.value = utils.removeTimeDataFromDate(agreementDetailStore.RegulationDt);
            regulationNm.value = agreementDetailStore.RegulationNm;
            regulationContent.value = agreementDetailStore.RegulationContent;
        } else {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
            return;
        }
    });

    async function submit() {
        // ボタン連打ガード制御
        disabledStore.buttonTemporaryDisabled();
        
        // ユーザー利用規定同意登録APIのリクエストボディを生成する。
        const requestBody = {
            RegulationPk: loginStore.regulationPk, // ログイン画面などでユーザー状態確認（ログイン）APIから事前に取得してある規定PK。
            // RegulationPk: setupAgreeStore.getRegulationPk,
            RegulationHistPk: agreementDetailStore.RegulationHistPk, // 本画面で取得した規定履歴PK。
        };

        // ユーザー利用規定同意登録APIをコールして、本ユーザーの同意情報を登録する。
        const response = await setupAgreeStore.setupUserAgreementUpdateAction(app.appContext.config.globalProperties.$constProperties.BASE_API_URL_S, requestBody);

        if (response.success) {
            // ユーザー状態確認(ログイン)APIをコールして、ユーザーの各種状態を取得する。
            const response2 = await loginStore.getUserStatusByLoginAction(app.appContext.config.globalProperties.$constProperties.BASE_API_URL_S, null);

            if (response2.success) {
                // 有効な契約が存在するか判定する。
                // 有効化契約が存在しない場合は、エラーメッセージを表示して、画面に留まる。
                if (response2.responseBody.ContractAssigned) {
                    // Storeを初期化する。
                    agreementDetailStore.reset();

                    // ユーザー情報更新要否を判定する。
                    if (response2.responseBody.UserInfoNeed2Update) {
                        // ユーザー情報入力画面へ遷移する。
                        router.push({ name: 'setupUser' });
                    } else if (response2.responseBody.ParentAgreeNeed2Check) {
                        // 親権者同意確認要否を判定する。

                        // 親権者同意画面へ遷移する。
                        router.push({ name: 'setupParent' });
                    } else {
                        // 上記のいずれも不要、もしくは、すでに同意済み等で設定が不要な場合。
                        // ホーム画面へ遷移する。
                        router.push({ name: 'home' });
                    }
                } else {
                    // 有効な契約が存在しない場合はエラーにする。
                    messageDialog('error', i18n.global.t('common.message.isContractAssignError'));

                    // ログアウト処理を実行する。
                    const logoutUrl = await headerStore.logoutAction();

                    // ログアウトの遷移先に応じて移動する。
                    if (logoutUrl.href) {
                        location.href = logoutUrl.url;
                    } else {
                        router.push({ name: logoutUrl.url, query: logoutUrl.query });
                    }
                }
            } else {
                messageDialog('error', response.errorMessage);
                if (response.redirect) {
                    location.href = response.redirectUrl;
                }
                return;
            }
        } else {
            messageDialog('error', response.errorMessage);
            if (response.redirect) {
                location.href = response.redirectUrl;
            }
            return;
        }
    }

    function agreeChange() {
        if (agreeDisabledFlag.value) {
            agreeDisabledFlag.value = false;
        } else {
            agreeDisabledFlag.value = true;
        }
    }
</script>

<template>
    <section id="main" class="bg">
        <MainHeader />
        <div id="contents">
            <div class="whiteBox">
                <h3>
                    <span>{{ $t('setupAgree.parts.pageTitle1') }}</span>
                </h3>
                <section class="whiteBoxContents">
                    <p class="pageInfo">
                        {{ $t('setupAgree.parts.pageInformation1') }}<br />
                        {{ $t('setupAgree.parts.pageInformation2') }}<br />
                        {{ $t('setupAgree.parts.pageInformation3') }}
                    </p>
                    <div class="regulationBox">
                        <span class="regulationDate">{{ regulationDt }}</span>
                        <span class="regulationTitle">{{ regulationNm }}</span>
                        <div class="regulationBody">{{ regulationContent }}</div>
                    </div>
                    <ul class="btnArea center">
                        <li>
                            <!--同意チェックボックス-->
                            <input type="checkbox" id="checkbox1" class="checkbox" @change="agreeChange" />
                            <label for="checkbox1" class="checkbox label">{{ $t('setupAgree.parts.checkboxLabel1') }}</label>
                        </li>
                    </ul>
                </section>
                <section class="whiteBoxFooter">
                    <ul class="btnArea center">
                        <li>
                            <!--登録-->
                            <button type="button" class="btnPrimary" @click="submit" :disabled="agreeDisabledFlag || disabledStore.isDisabled">
                                {{ $t('common.parts.entry') }}
                            </button>
                        </li>
                    </ul>
                </section>
            </div>
        </div>
    </section>
</template>
<style scoped>
    #contents .whiteBox .whiteBoxContents > .regulationBox {
        max-height: calc(100% - 186px);
    }
    /***** スマートフォン用サイズ -start- *****/
    @media screen and (max-width: 560px) {
        #contents .whiteBox .whiteBoxContents > .regulationBox {
            height: calc(100% - 213px) !important;
        }
    }
</style>
